import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2,
  Download, Package, FileText, Newspaper,
} from "lucide-react";

import { fetchIssueDetail, fetchPageArticles, type PageInfo } from "../api/issues";
import { tokenStore, api } from "../api/client";
import { useAuth } from "../auth/useAuth";
import { Spinner } from "../components/Spinner";
import { DownloadDialog } from "../components/DownloadDialog";
import { ArticleLightbox } from "../components/ArticleLightbox";
import { shortDate } from "../lib/format";

/** Append ?t=<jwt> for <img> tags. */
function withToken(url: string): string {
  const at = tokenStore.access;
  if (!at) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}t=${encodeURIComponent(at)}`;
}

// Discrete zoom levels — fit means "scale to viewport".
type ZoomMode = number | "fit";
const ZOOM_LEVELS: ReadonlyArray<ZoomMode> = ["fit", 1, 1.5, 2, 3];

export function Reader() {
  const params = useParams<{ issueId: string }>();
  const issueId = Number(params.issueId);
  const { me } = useAuth();

  const q = useQuery({
    queryKey: ["issue", issueId],
    queryFn: () => fetchIssueDetail(issueId),
    enabled: !!issueId,
  });

  const [index, setIndex] = useState(0);
  const [zoomIdx, setZoomIdx] = useState(0);            // default = "fit"
  const [imgErrors, setImgErrors] = useState<Set<number>>(new Set());

  // Image source preference. The CDN mirror lags for freshly-crawled pages
  // (today's papers land on the SMB share within seconds but take longer
  // to appear on player.visualmediamonitor.com). Default to PROXY for
  // recent issues so the user never sees a blank placeholder while
  // waiting for the CDN to sync. Older issues use the CDN (faster, no
  // API hit). Hard override via /api/auth/me.image_source_mode.
  const preferProxy = useMemo(() => {
    if (me?.image_source_mode === "proxy") return true;
    if (me?.image_source_mode === "cdn") return false;
    // Auto: proxy for last 3 days, CDN for older
    if (!q.data?.business_date) return true;
    const issueDate = new Date(q.data.business_date);
    const ageMs = Date.now() - issueDate.getTime();
    return ageMs < 3 * 24 * 3600 * 1000;
  }, [me?.image_source_mode, q.data?.business_date]);

  function pickSrc(p: PageInfo): string {
    if (preferProxy || imgErrors.has(p.artifact_id) || !p.image_url) {
      return withToken(p.proxy_url);
    }
    return p.image_url;
  }

  // Download dialog state
  const [dlOpen, setDlOpen] = useState(false);
  const [dlMode, setDlMode] = useState<"page" | "issue" | "pdf">("page");
  const [pageDims, setPageDims] = useState<{ w: number; h: number } | null>(null);

  const pages: PageInfo[] = useMemo(() => q.data?.pages ?? [], [q.data]);
  const page = pages[index];
  const issue = q.data;

  /* ── Article layer ──────────────────────────────────────────────────
     Fetched per page, keyed on artifact_id, which PageInfo already carries. Most pages
     have no layer and return an empty list, so the control below only appears where we
     actually hold crops - which is the ten publishers that ship them. */
  const [articlesOn, setArticlesOn] = useState(true);
  const [lbIdx, setLbIdx] = useState<number | null>(null);

  const artQ = useQuery({
    queryKey: ["page-articles", page?.artifact_id ?? 0],
    queryFn: () => fetchPageArticles(page!.artifact_id),
    enabled: !!page,
    staleTime: 5 * 60_000,
  });
  const zones = useMemo(() => artQ.data?.articles ?? [], [artQ.data]);
  const layerW = artQ.data?.page_w ?? 0;
  const layerH = artQ.data?.page_h ?? 0;
  // Percentages are only meaningful if we know the page size the boxes were measured
  // against. Without it the overlay would be positioned against nothing.
  const canOverlay = zones.length > 0 && layerW > 0 && layerH > 0;

  // Close the lightbox when the page changes, otherwise it would show an article from
  // the page the user just left.
  useEffect(() => { setLbIdx(null); }, [page?.artifact_id]);

  useEffect(() => { setIndex(0); }, [issueId]);

  const go = useCallback((dir: 1 | -1) => {
    setIndex((i) => {
      const next = i + dir;
      return (next < 0 || next >= pages.length) ? i : next;
    });
  }, [pages.length]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (dlOpen) return;
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); go(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
      else if (e.key === "Home") setIndex(0);
      else if (e.key === "End")  setIndex(Math.max(0, pages.length - 1));
      else if (e.key === "+" || e.key === "=") setZoomIdx(z => Math.min(ZOOM_LEVELS.length - 1, z + 1));
      else if (e.key === "-") setZoomIdx(z => Math.max(0, z - 1));
      else if (e.key === "0") setZoomIdx(0);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, pages.length, dlOpen]);

  const imgSrc = useMemo(() => {
    if (!page) return null;
    return pickSrc(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, imgErrors, preferProxy]);

  const onImgError = useCallback(() => {
    if (!page) return;
    setImgErrors((s) => new Set(s).add(page.artifact_id));
  }, [page]);

  /* When opening the Modify dialog for a page, fetch its real dimensions. */
  const openPageDialog = useCallback(async () => {
    if (!page) return;
    try {
      const r = await api.get<{ width: number; height: number }>(
        `/api/image/${page.artifact_id}/dims`
      );
      setPageDims({ w: r.data.width, h: r.data.height });
    } catch {
      setPageDims({ w: 2400, h: 3600 }); // fallback default
    }
    setDlMode("page");
    setDlOpen(true);
  }, [page]);

  const openBulkDialog = useCallback(async (mode: "issue" | "pdf") => {
    if (!page) {
      setPageDims({ w: 2400, h: 3600 });
    } else {
      try {
        const r = await api.get<{ width: number; height: number }>(
          `/api/image/${page.artifact_id}/dims`
        );
        setPageDims({ w: r.data.width, h: r.data.height });
      } catch {
        setPageDims({ w: 2400, h: 3600 });
      }
    }
    setDlMode(mode);
    setDlOpen(true);
  }, [page]);

  /* "Fit to viewport" needs the available height — measure the main pane. */
  const mainRef = useRef<HTMLDivElement | null>(null);
  const [paneHeight, setPaneHeight] = useState<number>(800);
  useEffect(() => {
    function measure() {
      if (mainRef.current) setPaneHeight(mainRef.current.clientHeight - 32);
    }
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  if (q.isLoading) {
    return <div className="flex justify-center py-16"><Spinner label="Loading issue" /></div>;
  }
  if (q.isError || !issue) {
    return (
      <div className="np-banner">
        <span className="font-mono text-[10px] uppercase tracking-folio mr-2 text-[var(--accent)]">Error</span>
        Could not load issue {issueId}.
      </div>
    );
  }

  const issueBase = `${issue.channel_name.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}_${(issue.city ?? "edition").replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}_${issue.business_date}`;
  const pageBase  = `${issueBase}_pg-${String(index + 1).padStart(2, "0")}`;

  const zoom = ZOOM_LEVELS[zoomIdx]!;

  return (
    <div className="flex flex-col h-full">
      {/* ── Slim header ───────────────────────────────────────────── */}
      <header
        className="px-6 md:px-10 py-2.5 border-b flex items-center gap-4 flex-wrap"
        style={{ borderColor: "var(--rule)", background: "var(--bg-card)" }}
      >
        <Link
          to={`/browse/${issue.channel_id}/${issue.edition_id}`}
          className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-muted)] hover:text-[var(--fg)] inline-flex items-center gap-1.5 shrink-0"
        >
          <ArrowLeft size={11} /> {issue.city}
        </Link>

        <div className="w-px h-4 bg-[var(--rule)]" />

        <div className="flex items-baseline gap-2 min-w-0 flex-1">
          <h1 className="font-display text-[18px] md:text-[20px] font-semibold leading-tight truncate">
            {issue.channel_name}
          </h1>
          <span className="font-display italic text-[15px] text-[var(--fg-muted)] hidden md:inline">
            {issue.city}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] hidden md:inline ml-1">
            · {shortDate(issue.business_date)}
          </span>
        </div>

        {/* nav */}
        <div className="flex items-center gap-1.5">
          <ToolButton onClick={() => go(-1)} disabled={index === 0} aria="Previous">
            <ChevronLeft size={14} />
          </ToolButton>
          <div className="font-mono text-[11px] uppercase tracking-folio text-[var(--fg-muted)] tabular-nums w-16 text-center">
            {index + 1}<span className="text-[var(--fg-faint)]">/{pages.length}</span>
          </div>
          <ToolButton onClick={() => go(1)} disabled={index >= pages.length - 1} aria="Next">
            <ChevronRight size={14} />
          </ToolButton>

          <div className="w-px h-4 bg-[var(--rule)] mx-1" />

          <ToolButton onClick={() => setZoomIdx(z => Math.max(0, z - 1))} disabled={zoomIdx === 0} aria="Zoom out">
            <ZoomOut size={13} />
          </ToolButton>
          <button
            type="button"
            onClick={() => setZoomIdx(0)}
            className="font-mono text-[11px] tabular-nums w-14 text-center text-[var(--fg-muted)] hover:text-[var(--fg)]"
            title="Reset to fit"
          >
            {zoom === "fit" ? "Fit" : `${Math.round(zoom * 100)}%`}
          </button>
          <ToolButton onClick={() => setZoomIdx(z => Math.min(ZOOM_LEVELS.length - 1, z + 1))} disabled={zoomIdx === ZOOM_LEVELS.length - 1} aria="Zoom in">
            <ZoomIn size={13} />
          </ToolButton>
          <ToolButton onClick={() => setZoomIdx(0)} aria="Reset zoom">
            <Maximize2 size={13} />
          </ToolButton>

          {canOverlay && (
            <>
              <div className="w-px h-4 bg-[var(--rule)] mx-1" />
              <button
                onClick={() => setArticlesOn(v => !v)}
                className="np-btn-ghost"
                title={articlesOn
                  ? "Hide article zones"
                  : "Show article zones - click a story to read it full size"}
                style={articlesOn
                  ? { color: "var(--accent)", borderColor: "var(--accent)" }
                  : undefined}
              >
                <Newspaper size={12} /> {zones.length} article{zones.length === 1 ? "" : "s"}
              </button>
            </>
          )}

          <div className="w-px h-4 bg-[var(--rule)] mx-1" />

          <button onClick={openPageDialog} className="np-btn-ghost" title="Customise download for this page">
            <Download size={12} /> Page
          </button>
          <button onClick={() => void openBulkDialog("issue")} className="np-btn-ghost" title="Download all pages as a zip">
            <Package size={12} /> All
          </button>
          <button onClick={() => void openBulkDialog("pdf")} className="np-btn-ghost" title="Bundle all pages into a single PDF">
            <FileText size={12} /> PDF
          </button>
        </div>
      </header>

      {/* ── Body — thumbnail strip + viewport ───────────────────── */}
      <div className="grid grid-cols-[100px,1fr] flex-1 min-h-0">
        {/* thumbs */}
        <aside
          className="overflow-y-auto border-r"
          style={{ borderColor: "var(--rule)", background: "var(--bg)" }}
        >
          <div className="flex flex-col gap-1.5 p-2">
            {pages.map((p, i) => (
              <button
                key={p.artifact_id}
                onClick={() => setIndex(i)}
                aria-current={i === index ? "page" : undefined}
                className="group block text-left"
              >
                <div
                  className="relative border overflow-hidden"
                  style={{
                    borderColor: i === index ? "var(--accent)" : "var(--rule)",
                    aspectRatio: "2 / 3",
                    background: "var(--bg-inset)",
                  }}
                >
                  <img
                    src={pickSrc(p)}
                    alt={`p${i + 1}`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                    onError={() => setImgErrors((s) => new Set(s).add(p.artifact_id))}
                  />
                  {i === index && (
                    <div className="absolute inset-0 ring-2 ring-[var(--accent)] ring-inset pointer-events-none" />
                  )}
                </div>
                <div className="font-mono text-[9px] text-center mt-0.5 tabular-nums text-[var(--fg-muted)]">
                  {String(i + 1).padStart(2, "0")}
                </div>
              </button>
            ))}
          </div>
        </aside>

        {/* main pane */}
        <section
          ref={mainRef}
          className="relative overflow-auto"
          style={{ background: "var(--bg-inset)" }}
        >
          {page && imgSrc ? (
            <div
              className="flex justify-center items-start p-4"
              style={{ minWidth: "min-content" }}
            >
              <div className="np-card overflow-hidden shadow-plate transition-all duration-200 relative">
                <img
                  src={imgSrc}
                  alt={`Page ${index + 1}`}
                  onError={onImgError}
                  style={
                    zoom === "fit"
                      ? { height: paneHeight, width: "auto", display: "block" }
                      : { height: paneHeight * (zoom as number), width: "auto", display: "block", maxWidth: "none" }
                  }
                />

                {/* Article zones.
                    Positioned in PERCENTAGES of the stored page size, which is what makes
                    this work across "fit" and the five discrete zoom levels with no
                    recalculation: the overlay is inset-0 on the image's own box, so the
                    percentages track whatever size the browser renders the image at. */}
                {articlesOn && canOverlay && (
                  <div className="absolute inset-0">
                    {zones.map((zn, i) => (
                      <button
                        key={zn.article_id}
                        onClick={() => setLbIdx(i)}
                        title={zn.headline || `Article ${zn.article_no} - click to read`}
                        aria-label={zn.headline || `Article ${zn.article_no}`}
                        className="absolute group"
                        style={{
                          left:   `${(zn.bbox_x / layerW) * 100}%`,
                          top:    `${(zn.bbox_y / layerH) * 100}%`,
                          width:  `${(zn.bbox_w / layerW) * 100}%`,
                          height: `${(zn.bbox_h / layerH) * 100}%`,
                          border: "1px solid color-mix(in srgb, var(--accent) 55%, transparent)",
                          background: "color-mix(in srgb, var(--accent) 8%, transparent)",
                          cursor: "pointer",
                        }}
                      >
                        <span
                          className="absolute left-0 top-0 font-mono text-[9px] px-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ background: "var(--accent)", color: "white" }}
                        >
                          {String(zn.article_no).padStart(2, "0")}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <Spinner />
            </div>
          )}
        </section>
      </div>

      {/* ── Footer hint ───────────────────────────────────────────── */}
      <div
        className="px-6 md:px-10 py-1.5 border-t font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] flex justify-between"
        style={{ borderColor: "var(--rule)", background: "var(--bg-card)" }}
      >
        <span>
          ← / → navigate · 0 fit · + / − zoom
          {canOverlay ? " · click an article to read it full size" : ""}
        </span>
        {page && imgErrors.has(page.artifact_id) && (
          <span className="text-[var(--accent)]">[via api proxy]</span>
        )}
      </div>

      {/* Article lightbox - the crop at full resolution */}
      {lbIdx !== null && canOverlay && (
        <ArticleLightbox
          zones={zones}
          index={lbIdx}
          onIndex={setLbIdx}
          onClose={() => setLbIdx(null)}
          label={`${issue?.channel_name ?? ""} ${issue?.city ?? ""} p${String(index + 1).padStart(2, "0")}`}
        />
      )}

      {/* Download dialog */}
      <DownloadDialog
        open={dlOpen}
        onClose={() => setDlOpen(false)}
        origW={pageDims?.w ?? 2400}
        origH={pageDims?.h ?? 3600}
        mode={dlMode}
        artifactId={page?.artifact_id}
        issueId={issue.issue_id}
        suggestedBase={(dlMode === "issue" || dlMode === "pdf") ? issueBase : pageBase}
        channelId={issue.channel_id}
        editionId={issue.edition_id}
        channelName={issue.channel_name}
        editionName={issue.city ?? issue.edition_name ?? undefined}
      />
    </div>
  );
}

function ToolButton({
  onClick, disabled, aria, children,
}: {
  onClick: () => void;
  disabled?: boolean;
  aria: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={aria}
      className="np-btn-ghost p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}
